import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AlertCircle, ArrowLeft, WifiOff } from "lucide-react";

import "./UserTestPage.scss";
import TestResults from "../TestResults/TestResults";
import axiosInstance from "../../axiosInstace";
import { useAuth } from "../../contexts/AuthContext";

import {
  toInt,
  getQuestionType,
  flattenQuestions,
  parseServerDate,
} from "./testUtils";
import TestHeader from "../../components/TestHeader/Testheader";
import TestSidebar from "../../components/TestSidebar/Testsidebar";
import QuestionCard from "../../components/QuestionCard/QuestionCard";
import TestFooter from "../../components/TestFooter/TestFooter";
import SubmitDialog from "../../components/SubmitDialog/SubmitDialog";
import IntroScreen from "../../components/IntroScreen/IntroScreen";

const API_BASE = process.env.REACT_APP_API_USER_URL;

// Autosave cadence: a quick debounce right after an edit, plus a periodic
// backstop in case the debounce never fires (e.g. the user just idles on
// one screen for a while).
const SAVE_DEBOUNCE_MS = 1500;
const AUTOSAVE_INTERVAL_MS = 30000;
const LOW_TIME_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Turns the flat `responses` array returned by the progress/start endpoints
 * back into the `{ [questionId]: value }` shape the UI works with.
 *
 * `questionTypeById` (Map<questionId, questionType>) comes from
 * `getQuestionType` in testUtils, so a multi-select question with exactly
 * one saved option still resumes as an array (not a bare number). Questions
 * missing from the map (shouldn't normally happen) fall back to the old
 * row-count heuristic so resume never silently drops an answer.
 */
function buildAnswersFromResponses(responses, questionTypeById) {
  const grouped = {};
  (responses || []).forEach((r) => {
    const qid = toInt(r.questionId);
    const oid = toInt(r.answerOptionId);
    if (qid == null || oid == null) return;
    if (!grouped[qid]) grouped[qid] = [];
    grouped[qid].push(oid);
  });
  const answers = {};
  Object.entries(grouped).forEach(([qid, arr]) => {
    const type = questionTypeById?.get(Number(qid));
    const isMulti = type ? type === "MULTI_CHOICE" : arr.length > 1;
    answers[qid] = isMulti ? arr : arr[0];
  });
  return answers;
}

/** Flattens `{ [questionId]: number | number[] }` into API row shape. */
function buildResponsesPayload(answersObj) {
  const out = [];
  Object.entries(answersObj || {}).forEach(([questionId, value]) => {
    const qid = toInt(questionId);
    if (qid == null) return;
    if (Array.isArray(value)) {
      value.forEach((oid) => {
        const optId = toInt(oid);
        if (optId != null) out.push({ questionId: qid, answerOptionId: optId });
      });
    } else {
      const optId = toInt(value);
      if (optId != null) out.push({ questionId: qid, answerOptionId: optId });
    }
  });
  return out;
}

function formatRemaining(ms) {
  if (ms == null) return null;
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
}

const UserTestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── data / lifecycle state ──────────────────────────────────────────────
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [hasAlreadyTaken, setHasAlreadyTaken] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // ── server-authoritative attempt/timer state ────────────────────────────
  // testResultId, expiresAt, and the clock-offset are what make the timer
  // trustworthy: the countdown always derives from `expiresAt` (set by the
  // backend), never from a locally-invented duration, and every save/submit
  // call is validated server-side regardless of what the client displays.
  const [expiresAt, setExpiresAt] = useState(null); // Date | null
  const [remainingMs, setRemainingMs] = useState(null);
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saved' | 'saving'

  const testResultIdRef = useRef(null);
  const clockOffsetMsRef = useRef(0); // serverTime - clientTime, captured at start/resume
  const autoSubmitTriggeredRef = useRef(false);
  const beginningRef = useRef(false); // guards beginAttempt against overlapping calls
  const savingRef = useRef(false);
  const saveDebounceRef = useRef(null);

  // ── answers: single-select questions store a number; multi-select store
  // an array of numbers, keyed by questionId ──
  const [userAnswers, setUserAnswers] = useState({});
  const userAnswersRef = useRef({});
  useEffect(() => {
    userAnswersRef.current = userAnswers;
  }, [userAnswers]);

  const [currentFlatIndex, setCurrentFlatIndex] = useState(0);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const stageRef = useRef(null);

  const storageKey = useMemo(
    () => (testId ? `hrlabs:test-attempt:${testId}` : null),
    [testId],
  );

  // ── derived data ──────────────────────────────────────────────────────
  const flatQuestions = useMemo(
    () => flattenQuestions(testData?.sections || []),
    [testData],
  );
  const totalQuestions = flatQuestions.length;
  const answeredQuestionIds = useMemo(() => {
    const set = new Set();
    Object.entries(userAnswers).forEach(([qid, val]) => {
      const hasValue = Array.isArray(val) ? val.length > 0 : val != null;
      if (hasValue) set.add(toInt(qid));
    });
    return set;
  }, [userAnswers]);
  const answeredCount = answeredQuestionIds.size;
  const overallProgress =
    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const current = flatQuestions[currentFlatIndex];
  const currentQuestion = current?.question;
  const currentType = currentQuestion ? getQuestionType(currentQuestion) : null;
  const isLastQuestion = currentFlatIndex === totalQuestions - 1;

  // Used to resolve resumed answers to the correct single/array shape.
  const questionTypeById = useMemo(() => {
    const map = new Map();
    flatQuestions.forEach((fq) => {
      const qid = toInt(fq.question?.questionId);
      if (qid != null) map.set(qid, getQuestionType(fq.question));
    });
    return map;
  }, [flatQuestions]);

  // ── load ──────────────────────────────────────────────────────────────
  // Holds the attempt id (testResultId, aka the progress endpoint's
  // `testLogsId`) when /tests/getall tells us this test already has an
  // IN_PROGRESS attempt server-side. Picked up by the effect below once
  // testData has actually loaded, so resumeFromProgress can run.
  //
  // NOTE: this assumes /tests/getall's entries carry `status: "IN_PROGRESS"`
  // and `testResultId` — same field names as the progress-endpoint payload.
  // Adjust the two field reads below if the real /tests/getall shape differs.
  const pendingResumeIdRef = useRef(null);

  const loadTestData = useCallback(async () => {
    setLoading(true);
    try {
      try {
        const userTestsResponse = await axiosInstance.get(
          `${API_BASE}/tests/getall`,
        );
        if (userTestsResponse.data?.data) {
          const submittedTest = userTestsResponse.data.data.find(
            (t) => String(t.testId) === String(testId),
          );
          if (submittedTest?.completed) {
            setHasAlreadyTaken(true);
            setTestCompleted(true);
            setResults({
              testId,
              sections: [],
              sectionTitles: [],
              sectionInterpretations: [],
              sectionRecommendations: [],
              interpretationTitle: "",
              interpretationDescription:
                "You have previously completed this assessment. Your results have been recorded.",
              recommendationTitle: "",
              recommendationDescription:
                "Consider exploring other assessments or retaking this one to see how your responses may have changed over time.",
              interpretation:
                "You have previously completed this assessment. Your results have been recorded.",
              recommendation:
                "Consider exploring other assessments or retaking this one to see how your responses may have changed over time.",
            });
            setLoading(false);
            return;
          }
          if (
            submittedTest?.status === "IN_PROGRESS" &&
            submittedTest?.testResultId
          ) {
            pendingResumeIdRef.current = submittedTest.testResultId;
          }
        }
      } catch {
        // No history endpoint access, or user hasn't taken anything yet — proceed to fresh load.
      }

      const response = await axiosInstance.get(`${API_BASE}/tests/${testId}`);
      if (response.data?.data) {
        setTestData(response.data.data);
        setError(null);
        setHasAlreadyTaken(false);
      } else {
        throw new Error(response.data?.message || "Failed to load test data");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load test. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    if (testId) loadTestData();
  }, [testId, loadTestData]);

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Warn before leaving with unsaved-but-incomplete progress.
  useEffect(() => {
    const handler = (e) => {
      if (
        testStarted &&
        !testCompleted &&
        answeredCount > 0 &&
        answeredCount < totalQuestions
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [testStarted, testCompleted, answeredCount, totalQuestions]);

  // ── cross-tab sync ───────────────────────────────────────────────────────
  // If the attempt gets submitted (and its pointer removed) from another
  // tab, don't let this tab keep running a countdown for a dead attempt.
  useEffect(() => {
    if (!storageKey) return;
    const handler = (e) => {
      if (e.key !== storageKey) return;
      if (e.newValue === null && testStarted && !testCompleted) {
        toast.info("This assessment was already submitted in another tab.");
        navigate("/dashboard");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, testStarted, testCompleted, navigate]);

  // ── shared: apply attempt data (from /tests/start OR /tests/progress) ──
  // Both endpoints return the same attempt shape (testResultId, startTime,
  // endTime/attemptDuration, testLogType, responses[]), so however we got
  // here — clicking Start, silently resuming via the local storage pointer,
  // or resuming via the progress endpoint — the same code turns it into
  // component state. `offset` (serverTime - clientTime) is computed
  // differently by each caller, since only a genuinely fresh /start call
  // can safely be used to measure real clock drift.
  const applyAttemptData = useCallback(
    (data, { isResumed, offset }) => {
      const serverStartMs = parseServerDate(data.startTime)?.getTime();
      const expiresMs = data.endTime
        ? parseServerDate(data.endTime)?.getTime()
        : data.attemptDuration?.seconds != null
          ? serverStartMs + data.attemptDuration.seconds * 1000
          : null;

      testResultIdRef.current = data.testResultId;
      clockOffsetMsRef.current = offset;
      autoSubmitTriggeredRef.current = false;

      if (storageKey) {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            testResultId: data.testResultId,
            clockOffsetMs: offset,
            startTime: data.startTime,
            expiresAtMs: expiresMs,
            testLogType: data.testLogType,
          }),
        );
      }

      setUserAnswers(
        isResumed
          ? buildAnswersFromResponses(data.responses, questionTypeById)
          : {},
      );
      setStartTime(parseServerDate(data.startTime));
      setExpiresAt(expiresMs ? new Date(expiresMs) : null);
      setTestStarted(true);
    },
    [storageKey, questionTypeById],
  );

  // /tests/start's response never carries saved answers — even when it
  // tells us an attempt is "already in progress", its `responses` field
  // comes back null. Only GET /tests/progress/{testResultId} actually
  // includes them, so both resume paths below call this to fetch the real
  // attempt data (with responses) before handing it to applyAttemptData.
  // Returns null on any failure so callers can fall back gracefully.
  const fetchProgressData = useCallback(async (testResultId) => {
    try {
      const res = await axiosInstance.get(
        `${API_BASE}/tests/progress/${testResultId}`,
      );
      const data = res.data?.data;
      return data?.testResultId ? data : null;
    } catch {
      return null;
    }
  }, []);

  // ── begin or resume an attempt ──────────────────────────────────────────
  // The backend's /start endpoint is idempotent: if an attempt is already
  // IN_PROGRESS it just hands back that same attempt (same startTime,
  // same endTime) instead of creating a new one. That means this one
  // function safely covers both "candidate clicks Start" and "we're
  // silently resuming after a refresh" — same call, same response shape.
  //
  // The one thing we must NOT do is treat a *resumed* attempt's startTime as
  // "now" on the server: it can be minutes old, so using it to measure clock
  // drift would produce a huge bogus offset and make the countdown look like
  // it reset. We only trust startTime for drift correction on a genuinely
  // fresh attempt (startTime is within a few seconds of our own clock, or
  // the backend didn't say "already in progress").
  const beginAttempt = useCallback(
    async ({ silent = false } = {}) => {
      const tId = Number(testData?.testId);
      if (!Number.isInteger(tId) || beginningRef.current || testStarted) return;
      beginningRef.current = true;
      if (!silent) setStarting(true);

      try {
        const clientBefore = Date.now();
        const res = await axiosInstance.post(`${API_BASE}/tests/start/${tId}`);
        const data = res.data?.data;
        if (!data?.testResultId) {
          throw new Error(res.data?.message || "Could not start the test.");
        }

        const serverStartMs = parseServerDate(data.startTime)?.getTime();
        // The backend tells us directly via `status: "IN_PROGRESS"` that
        // this is an existing attempt being handed back, not a fresh one —
        // that's the reliable signal. The message-text/time-gap checks are
        // kept only as a fallback in case some backend response omits
        // `status` (older endpoint versions, etc).
        const statusSaysResumed = data.status === "IN_PROGRESS";
        const messageSaysResumed = /already in progress/i.test(
          res.data?.message || "",
        );
        const startTimeGapMs = Number.isFinite(serverStartMs)
          ? Math.abs(clientBefore - serverStartMs)
          : Infinity;
        const isResumed =
          statusSaysResumed || messageSaysResumed || startTimeGapMs > 10000;

        let offset;
        if (isResumed) {
          // Reuse whatever drift we measured the first time this attempt
          // actually started, if we have it. Never recompute it here.
          let cachedOffset = 0;
          const cachedRaw = storageKey
            ? localStorage.getItem(storageKey)
            : null;
          if (cachedRaw) {
            try {
              cachedOffset = JSON.parse(cachedRaw)?.clockOffsetMs ?? 0;
            } catch {
              cachedOffset = 0;
            }
          }
          offset = cachedOffset;
        } else {
          offset = Number.isFinite(serverStartMs)
            ? serverStartMs - clientBefore
            : 0;
        }

        // /start's own payload has responses: null on a resumed attempt, so
        // pull the real attempt (with saved responses) from /progress before
        // applying it. Fall back to /start's data if that call fails, so a
        // resume still works — just without preselected answers.
        const effectiveData = isResumed
          ? (await fetchProgressData(data.testResultId)) || data
          : data;

        applyAttemptData(effectiveData, { isResumed, offset });

        if (isResumed) toast.info("Resuming your in-progress assessment.");
      } catch (err) {
        if (storageKey) localStorage.removeItem(storageKey);
        if (!silent) {
          toast.error(
            err.response?.data?.message ||
              err.message ||
              "Failed to start the test. Please try again.",
          );
        }
      } finally {
        beginningRef.current = false;
        setStarting(false);
      }
    },
    [testData, testStarted, storageKey, applyAttemptData, fetchProgressData],
  );

  // ── resume directly from GET /tests/progress/{testLogsId} ──────────────
  // The dashboard (`/tests/getall`) can already tell us a test has an
  // IN_PROGRESS attempt server-side, in which case we don't need to
  // round-trip through /tests/start at all — we go straight to the
  // progress endpoint and get timing + saved responses in one call. This
  // is also what lets resume work when the candidate comes back on a
  // different device/browser with no local storage pointer for this
  // attempt (the plain localStorage-driven auto-resume below can't help
  // there since it never sees a pointer to begin with).
  //
  // Returns true if it actually resumed an in-progress attempt, false if
  // there was nothing to resume (caller should fall back to beginAttempt).
  const resumeFromProgress = useCallback(
    async (testLogsId) => {
      const id = Number(testLogsId);
      if (!Number.isInteger(id) || beginningRef.current || testStarted) {
        return false;
      }
      beginningRef.current = true;
      try {
        const data = await fetchProgressData(id);
        if (!data || data.status !== "IN_PROGRESS") return false;

        // Same clock-drift caution as beginAttempt: this attempt's
        // startTime can be arbitrarily old, so we never derive drift from
        // it here — only reuse whatever offset we cached for it earlier.
        let cachedOffset = 0;
        const cachedRaw = storageKey ? localStorage.getItem(storageKey) : null;
        if (cachedRaw) {
          try {
            cachedOffset = JSON.parse(cachedRaw)?.clockOffsetMs ?? 0;
          } catch {
            cachedOffset = 0;
          }
        }

        applyAttemptData(data, { isResumed: true, offset: cachedOffset });
        toast.info("Resuming your in-progress assessment.");
        return true;
      } catch {
        // Swallow — caller falls back to the normal start/resume flow.
        return false;
      } finally {
        beginningRef.current = false;
      }
    },
    [testStarted, storageKey, applyAttemptData, fetchProgressData],
  );

  // Guards both auto-resume effects below so they don't race each other —
  // set as soon as either one decides it's handling the resume.
  const autoResumeCheckedRef = useRef(false);

  // On load, if the server told us (via /tests/getall, captured above) that
  // this test already has an IN_PROGRESS attempt, resume it straight from
  // GET /tests/progress/{testLogsId} — this is what makes resume work even
  // with no local storage pointer (new device/browser, cleared storage,
  // etc). Falls back to the normal beginAttempt flow if that turns out not
  // to be resumable after all.
  useEffect(() => {
    if (!testData || hasAlreadyTaken || testStarted) return;
    const id = pendingResumeIdRef.current;
    if (!id) return;
    pendingResumeIdRef.current = null;
    autoResumeCheckedRef.current = true; // don't also race the local-pointer effect below
    resumeFromProgress(id).then((resumed) => {
      if (!resumed) beginAttempt({ silent: true });
    });
  }, [
    testData,
    hasAlreadyTaken,
    testStarted,
    resumeFromProgress,
    beginAttempt,
  ]);

  // On load, if we have a local pointer suggesting an attempt is already
  // running, resume it automatically (silently) instead of waiting for the
  // candidate to hit Start again — this is what makes a page refresh
  // continue the same countdown rather than looking like a fresh start.
  useEffect(() => {
    if (
      !testData ||
      hasAlreadyTaken ||
      testStarted ||
      autoResumeCheckedRef.current ||
      !storageKey
    ) {
      return;
    }
    const raw = localStorage.getItem(storageKey);
    if (!raw) return; // no local hint — let the intro screen render normally
    autoResumeCheckedRef.current = true;
    beginAttempt({ silent: true });
  }, [testData, hasAlreadyTaken, testStarted, storageKey, beginAttempt]);

  // ── autosave ──────────────────────────────────────────────────────────
  const saveProgress = useCallback(async () => {
    const attemptId = testResultIdRef.current;
    if (!attemptId || savingRef.current) return;
    const responses = buildResponsesPayload(userAnswersRef.current);
    if (responses.length === 0) return;

    savingRef.current = true;
    setSaveStatus("saving");
    try {
      await axiosInstance.post(`${API_BASE}/tests/partial-save`, {
        testResultId: attemptId,
        responses,
      });
      setSaveStatus("saved");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 410) {
        // Backend has authoritatively decided the timer is over.
        handleExpiry();
      }
      // Otherwise leave saveStatus as "saving" — the next autosave cycle
      // (debounce or the 30s interval) will retry automatically.
    } finally {
      savingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleSave = () => {
    setSaveStatus("saving");
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(saveProgress, SAVE_DEBOUNCE_MS);
  };

  // Periodic backstop autosave, independent of the debounce above.
  useEffect(() => {
    if (!testStarted || testCompleted) return;
    const id = setInterval(saveProgress, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [testStarted, testCompleted, saveProgress]);

  useEffect(
    () => () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    },
    [],
  );

  // ── answer handling ──────────────────────────────────────────────────
  const goToFlatIndex = (index, { smooth = true } = {}) => {
    const clamped = Math.max(0, Math.min(totalQuestions - 1, index));
    setCurrentFlatIndex(clamped);
    setSidebarOpen(false);
    if (smooth && stageRef.current) {
      stageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSelectSingle = (questionId, optionId) => {
    const qid = toInt(questionId);
    const oid = toInt(optionId);
    if (qid == null || oid == null) return;
    setUserAnswers((prev) => ({ ...prev, [qid]: oid }));
    scheduleSave();

    // Auto-advance, unless this is the last question.
    if (!isLastQuestion) {
      setTimeout(() => goToFlatIndex(currentFlatIndex + 1), 260);
    }
  };

  const handleToggleMulti = (questionId, optionId) => {
    const qid = toInt(questionId);
    const oid = toInt(optionId);
    if (qid == null || oid == null) return;
    setUserAnswers((prev) => {
      const existing = Array.isArray(prev[qid]) ? prev[qid] : [];
      const next = existing.includes(oid)
        ? existing.filter((v) => v !== oid)
        : [...existing, oid];
      return { ...prev, [qid]: next };
    });
    scheduleSave();
  };

  const toggleBookmark = (questionId) => {
    setBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  // ── keyboard shortcuts ───────────────────────────────────────────────
  useEffect(() => {
    if (!testStarted || testCompleted || !currentQuestion) return;

    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName))
        return;

      if (e.key === "ArrowLeft") {
        goToFlatIndex(currentFlatIndex - 1);
      } else if (e.key === "ArrowRight") {
        goToFlatIndex(currentFlatIndex + 1);
      } else if (e.key === "Enter") {
        if (isLastQuestion) setSubmitDialogOpen(true);
        else goToFlatIndex(currentFlatIndex + 1);
      } else if (currentType === "RATING" && /^[1-9]$/.test(e.key)) {
        const options = currentQuestion.answerOptions || [];
        const match = options.find((o) =>
          String(o.answerText).trim().startsWith(e.key),
        );
        if (match)
          handleSelectSingle(currentQuestion.questionId, match.answerOptionId);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    testStarted,
    testCompleted,
    currentFlatIndex,
    currentQuestion,
    currentType,
    isLastQuestion,
  ]);

  // ── submit (manual confirm and auto-submit-on-expiry share this) ───────
  const unansweredList = useMemo(
    () =>
      flatQuestions
        .map((fq, idx) => ({ ...fq, flatIndex: idx }))
        .filter((fq) => !answeredQuestionIds.has(fq.question.questionId)),
    [flatQuestions, answeredQuestionIds],
  );

  const submitTest = useCallback(
    async ({ auto = false } = {}) => {
      const attemptId = testResultIdRef.current;
      if (!attemptId) {
        toast.error("No active attempt found.");
        return;
      }

      const resolvedUserId =
        user?.userDetailsId ?? user?.userId ?? user?.id ?? null;
      if (!auto && !Number.isInteger(Number(resolvedUserId))) {
        toast.error("User not identified. Please re-login.");
        setError("User not identified. Please re-login.");
        return;
      }

      setSubmitting(true);
      try {
        // Flush the latest answers before asking the backend to score —
        // submit-new scores whatever was last saved, it doesn't take answers.
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        await saveProgress();

        const effectiveNow = Date.now() + clockOffsetMsRef.current;
        const elapsedSeconds = startTime
          ? Math.max(0, Math.round((effectiveNow - startTime.getTime()) / 1000))
          : 0;

        const response = await axiosInstance.post(
          `${API_BASE}/tests/submit-new`,
          {
            testResultId: attemptId,
            // java.time.Duration is (de)serialized by Jackson as a plain
            // number of seconds by default (WRITE_DURATIONS_AS_TIMESTAMPS),
            // not as a { seconds, nano } object — sending an object here is
            // what triggers "Cannot deserialize value of type
            // `java.time.Duration` from Object value (token START_OBJECT)".
            timeTaken: elapsedSeconds,
          },
        );

        const testResults = response.data?.data || response.data;
        const sections = testResults.sections || [];
        setResults({
          testId: testResults.testId,
          testTotalScore: testResults.testTotalScore,
          totalScore: testResults.totalScore,
          interpretationTitle: testResults.interpretationTitle || "",
          interpretationDescription:
            testResults.interpretationDescription || "",
          recommendationTitle: testResults.recommendationTitle || "",
          recommendationDescription:
            testResults.recommendationDescription || "",
          // Kept for backwards compatibility with any UI still reading the
          // old flat "interpretation"/"recommendation" fields.
          interpretation: testResults.interpretationDescription || "",
          recommendation: testResults.recommendationDescription || "",
          sections,
          // Parallel arrays, derived from `sections`, for any UI still
          // expecting sectionTitles/sectionInterpretations/sectionRecommendations.
          sectionTitles: sections.map((s) => s.sectionTitle),
          sectionInterpretations: sections.map((s) => ({
            title: s.sectionInterpretationTitle,
            description: s.sectionInterpretationDescription,
          })),
          sectionRecommendations: sections.map((s) => ({
            title: s.sectionRecommendationTitle,
            description: s.sectionRecommendationDescription,
          })),
        });
        setTestCompleted(true);
        setSubmitDialogOpen(false);
        if (storageKey) localStorage.removeItem(storageKey);
        toast.success(
          auto
            ? "Time was up — your assessment was submitted automatically."
            : "Assessment completed successfully!",
        );
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to submit test. Please try again.";
        if (auto) {
          // Don't strand the candidate on a dead countdown — surface it and
          // allow a retry (manual submit button remains available).
          toast.error(`${errorMessage} Please try submitting manually.`);
          autoSubmitTriggeredRef.current = false;
        } else {
          setError(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [saveProgress, startTime, storageKey, user],
  );

  // ── server-driven countdown ──────────────────────────────────────────
  const handleExpiry = useCallback(() => {
    if (autoSubmitTriggeredRef.current || testCompleted) return;
    autoSubmitTriggeredRef.current = true;
    toast.info("Time's up — submitting your answers now.");
    submitTest({ auto: true });
  }, [testCompleted, submitTest]);

  useEffect(() => {
    if (!testStarted || testCompleted || !expiresAt) {
      setRemainingMs(null);
      return undefined;
    }
    const tick = () => {
      const effectiveNow = Date.now() + clockOffsetMsRef.current;
      const diff = expiresAt.getTime() - effectiveNow;
      if (diff <= 0) {
        setRemainingMs(0);
        handleExpiry();
      } else {
        setRemainingMs(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [testStarted, testCompleted, expiresAt, handleExpiry]);

  const timeRemainingLabel = useMemo(
    () => formatRemaining(remainingMs),
    [remainingMs],
  );
  const isLowTime = remainingMs != null && remainingMs <= LOW_TIME_THRESHOLD_MS;

  const handleBackToDashboard = () => navigate("/dashboard");

  const retakeTest = () => {
    if (storageKey) localStorage.removeItem(storageKey);
    testResultIdRef.current = null;
    clockOffsetMsRef.current = 0;
    autoSubmitTriggeredRef.current = false;

    setTestCompleted(false);
    setResults(null);
    setUserAnswers({});
    setCurrentFlatIndex(0);
    setTestStarted(false);
    setHasAlreadyTaken(false);
    setStartTime(null);
    setBookmarkedIds(new Set());
    setExpiresAt(null);
    setRemainingMs(null);
    beginningRef.current = false;
    autoResumeCheckedRef.current = false;
  };

  // ── render states ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="user-test-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading your assessment…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-test-page">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button className="retry-button" onClick={loadTestData}>
              Try again
            </button>
            <button className="back-button" onClick={handleBackToDashboard}>
              <ArrowLeft size={16} /> Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="user-test-page">
        <div className="error-container">
          <AlertCircle className="error-icon" />
          <h2>Test not found</h2>
          <p>
            The assessment you're looking for doesn't exist or has been removed.
          </p>
          <div className="error-actions">
            <button className="back-button" onClick={handleBackToDashboard}>
              <ArrowLeft size={16} /> Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (testCompleted) {
    return (
      <TestResults
        results={results}
        hasAlreadyTaken={hasAlreadyTaken}
        onRetakeTest={retakeTest}
        onBackToDashboard={handleBackToDashboard}
        testData={testData}
      />
    );
  }

  if (!testStarted) {
    return (
      <IntroScreen
        testData={testData}
        onBack={handleBackToDashboard}
        onStart={() => beginAttempt()}
        starting={starting}
      />
    );
  }

  // ── active test interface ───────────────────────────────────────────
  const rawAnswer = currentQuestion
    ? userAnswers[toInt(currentQuestion.questionId)]
    : undefined;
  const singleAnswer = Array.isArray(rawAnswer) ? undefined : rawAnswer;
  const multiAnswer = Array.isArray(rawAnswer) ? rawAnswer : [];

  return (
    <div className="user-test-page">
      {isOffline && (
        <div className="connection-banner">
          <WifiOff size={14} /> You're offline — answers are kept locally until
          you reconnect.
        </div>
      )}

      <TestHeader
        title={testData.testTitle || testData.title || "Assessment"}
        progressPercent={overallProgress}
        elapsedLabel="Elapsed"
        timeRemainingLabel={timeRemainingLabel}
        isLowTime={isLowTime}
        saveStatus={saveStatus}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
      />

      <div className="test-body">
        <TestSidebar
          sections={testData.sections || []}
          flatQuestions={flatQuestions}
          currentFlatIndex={currentFlatIndex}
          answeredQuestionIds={answeredQuestionIds}
          bookmarkedIds={bookmarkedIds}
          onJumpTo={goToFlatIndex}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          totalQuestions={totalQuestions}
          answeredCount={answeredCount}
        />

        <main className="test-stage" ref={stageRef}>
          <div className="stage-inner">
            {currentQuestion && (
              <QuestionCard
                key={currentQuestion.questionId}
                question={currentQuestion}
                questionNumber={currentFlatIndex + 1}
                singleAnswer={singleAnswer}
                multiAnswer={multiAnswer}
                onSelectSingle={(optionId) =>
                  handleSelectSingle(currentQuestion.questionId, optionId)
                }
                onToggleMulti={(optionId) =>
                  handleToggleMulti(currentQuestion.questionId, optionId)
                }
                isBookmarked={bookmarkedIds.has(currentQuestion.questionId)}
                onToggleBookmark={() =>
                  toggleBookmark(currentQuestion.questionId)
                }
              />
            )}
          </div>
        </main>
      </div>

      <TestFooter
        onPrevious={() => goToFlatIndex(currentFlatIndex - 1)}
        onNext={() => goToFlatIndex(currentFlatIndex + 1)}
        canGoPrevious={currentFlatIndex > 0}
        canGoNext={currentFlatIndex < totalQuestions - 1}
        isLastQuestion={isLastQuestion}
        onSubmit={() => setSubmitDialogOpen(true)}
        submitting={submitting}
        answeredCount={answeredCount}
        totalQuestions={totalQuestions}
      />

      <SubmitDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        onConfirm={() => submitTest()}
        unanswered={unansweredList}
        onJumpTo={(idx) => {
          setSubmitDialogOpen(false);
          goToFlatIndex(idx);
        }}
        submitting={submitting}
      />
    </div>
  );
};

export default UserTestPage;
